import type { paths } from "../lib/api/v1";

// 申込一覧 API のレスポンスの型。boron の swagger に名前付きの schema がないため、ここで 1 回だけ取り出す
export type LearningCoachApplicationRequestsResponse =
  paths["/api/v1/sections/{section_id}/learning_coach/application_requests"]["get"]["responses"]["200"]["content"]["application/json"];

export type LearningCoachApplicationRequest =
  LearningCoachApplicationRequestsResponse["applicationRequests"][number];

// 学習コーチングの申込一覧の状態と、画面に出す表示名の対応表。
// 状態の値の意味づけは boron / learning-coach が持ち、ここでは表示名に置き換えるだけにする。
// 表示名と見せ方は Figma のレビューで決まったもの。契約手続き中・契約済みは、トライアルを本契約と誤解させないよう
// 申込手続き中・申込済みに言い換えた（Figma の PdM コメント、2026-09-25）。処理エラーは、エラーの印象を避け
// 当社が対応していることを伝えるよう運営対応中にした（2026-09-30）。変わったらこのファイルだけを直す

export type LearningCoachApplicationRequestStatus =
  LearningCoachApplicationRequest["status"];

// 絞り込みの選択肢の並び（仕様書 No.1 の状態の列の順）
export const LEARNING_COACH_APPLICATION_REQUEST_STATUSES: readonly LearningCoachApplicationRequestStatus[] =
  [
    "pending_linkage",
    "awaiting_guardian_application",
    "contract_in_progress",
    "contracted",
    "processing_error",
  ];

const STATUS_LABELS: Record<LearningCoachApplicationRequestStatus, string> = {
  pending_linkage: "連携待ち",
  awaiting_guardian_application: "保護者申込待ち",
  contract_in_progress: "申込手続き中",
  contracted: "申込済み",
  processing_error: "運営対応中",
};

// 自分が起票した先生メール承認待ちの行にだけ添える補足（LIST-04。判定はバックエンドの awaitingMyApproval）
const AWAITING_MY_APPROVAL_LABEL = "先生メール承認待ち";

const WAITING_REASON_LABELS: Record<
  NonNullable<LearningCoachApplicationRequest["waitingReason"]>,
  string
> = {
  student_app: "生徒アプリ連携",
  guardian_line: "保護者LINE連携",
  both: "生徒アプリ連携・保護者LINE連携",
};

export const toStatusLabel = (
  status: LearningCoachApplicationRequestStatus,
): string => STATUS_LABELS[status];

// 一覧の「状態」列の表示。自分の承認待ちの補足と待機理由は括弧で添える。
// 承認待ちかどうかは補足（supplement）ではなく awaitingMyApproval で決める（LIST-04）
export const toApplicationRequestStateLabel = ({
  status,
  awaitingMyApproval,
  waitingReason,
}: Pick<
  LearningCoachApplicationRequest,
  "status" | "awaitingMyApproval" | "waitingReason"
>): string => {
  const detail =
    (awaitingMyApproval && AWAITING_MY_APPROVAL_LABEL) ||
    (waitingReason && WAITING_REASON_LABELS[waitingReason]);
  return detail
    ? `${STATUS_LABELS[status]}（${detail}）`
    : STATUS_LABELS[status];
};

// 状態の列に付けるアイコン。自分の承認待ちには注意、申込済みには成功を付け、それ以外は付けない（Figma #29）
export const toApplicationRequestStateIcon = ({
  status,
  awaitingMyApproval,
}: Pick<LearningCoachApplicationRequest, "status" | "awaitingMyApproval">):
  | "icon-attention"
  | "icon-success"
  | null => {
  if (awaitingMyApproval) {
    return "icon-attention";
  }
  return status === "contracted" ? "icon-success" : null;
};

// 一覧上部のアラートの件数。状態の絞り込みにかかわらず、取得した一覧全体で数える（LIST-04）
export const countAwaitingMyApproval = (
  applicationRequests: Pick<
    LearningCoachApplicationRequest,
    "awaitingMyApproval"
  >[],
): number =>
  applicationRequests.filter(({ awaitingMyApproval }) => awaitingMyApproval)
    .length;
